import type { ReactNode } from "react";
import { useUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import {
  Checkbox,
  type CheckboxComponentProps,
  useCheckboxCard,
} from "@tenant/ui";
import clsx from "clsx";

import defaultStyles from "./CheckboxCard.module.scss";

export interface IBaseCheckboxCardProps
  extends Omit<
    CheckboxComponentProps,
    "checked" | "defaultChecked" | "onCheckedChange"
  > {
  children: ReactNode;
  className?: string;
  /**
   * Overwrite Radix prop from using `CheckedState` to using `boolean`
   */
  onCheckedChange: (isChecked: boolean) => void;
}

export interface IControlledCheckboxCardProps extends IBaseCheckboxCardProps {
  /**
   * Overwrite Radix prop from `CheckedState` to `boolean`
   */
  checked: boolean;
  defaultChecked?: never;
}

export interface IUncontrolledCheckboxCardProps extends IBaseCheckboxCardProps {
  checked?: never;
  /**
   * Overwrite Radix prop from `CheckedState` to `boolean`
   */
  defaultChecked: boolean;
}

export type ICheckboxCardProps =
  | IControlledCheckboxCardProps
  | IUncontrolledCheckboxCardProps;

export const CheckboxCard = (props: ICheckboxCardProps) => {
  const { children, className, ...checkboxProps } = props;
  const COMPONENT_KEY = CoreUiComponents.CheckboxCard;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { context, handlers } = useCheckboxCard(props);

  return (
    <div
      className={clsx(
        block,
        cn("root", {
          checked: context.isChecked,
          disabled: context.isDisabled,
        }),
        className
      )}
      onClick={handlers.toggleIsChecked}
    >
      <Checkbox
        {...checkboxProps}
        checked={context.isChecked}
        className={cn("checkbox", {
          checked: context.isChecked !== false,
          disabled: context.isDisabled,
        })}
        onCheckedChange={handlers.setIsCheckedBoolean}
      />
      <div className={cn("content", { disabled: context.isDisabled })}>
        {children}
      </div>
    </div>
  );
};
